import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, 
  ChevronRight, 
  Eye, 
  EyeOff,
  Check,
  AlertCircle,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  BarChart2,
  Settings2
} from 'lucide-react';
import '../styles/login-v2.css';
import '../styles/password-reset.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const userId = searchParams.get('uid');
  const token = searchParams.get('token');

  // Mouse tracking para efectos parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Validar que tenemos token y uid
  useEffect(() => {
    if (!userId || !token) {
      setError('Enlace de recuperación inválido. Por favor, solicita uno nuevo.');
    }
  }, [userId, token]);

  // Validaciones de contraseña en tiempo real (memoizadas)
  const passwordValidations = useMemo(() => ({
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    match: password === confirmPassword && password.length > 0
  }), [password, confirmPassword]);

  const allValidationsPassed = Object.values(passwordValidations).every(v => v);

  // Componente ValidationItem (debe estar antes del useMemo que lo usa)
  const ValidationItem = ({ isValid, text }) => (
    <motion.div
      className="validation-item"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      {isValid ? (
        <CheckCircle size={16} className="text-green-500" />
      ) : (
        <XCircle size={16} className="text-red-400" />
      )}
      <span className={isValid ? 'text-green-600' : 'text-red-500'}>{text}</span>
    </motion.div>
  );

  // Memoizar el bloque de validaciones (DEBE estar fuera del condicional de success)
  const passwordRequirementsBlock = useMemo(() => {
    if (!password) return null;
    return (
      <motion.div 
        className="password-requirements"
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
      >
        <ValidationItem 
          isValid={passwordValidations.length} 
          text="Mínimo 8 caracteres" 
        />
        <ValidationItem 
          isValid={passwordValidations.uppercase} 
          text="Al menos una letra mayúscula" 
        />
        <ValidationItem 
          isValid={passwordValidations.lowercase} 
          text="Al menos una letra minúscula" 
        />
        <ValidationItem 
          isValid={passwordValidations.number} 
          text="Al menos un número" 
        />
        {confirmPassword && (
          <ValidationItem 
            isValid={passwordValidations.match} 
            text="Las contraseñas coinciden" 
          />
        )}
      </motion.div>
    );
  }, [password, confirmPassword, passwordValidations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allValidationsPassed) {
      setError('Por favor, cumple con todos los requisitos de contraseña.');
      return;
    }

    if (!userId || !token) {
      setError('Datos de recuperación inválidos.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          token: token,
          new_password: password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.detail || 'Error al restablecer la contraseña');
      }
    } catch (err) {
      setError('Error de conexión. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  // Elementos flotantes simplificados
  const floatingElements = [
    { icon: <Lock className="w-6 h-6" />, delay: 0, position: { left: '10%', top: '10%' } },
    { icon: <Shield className="w-5 h-5" />, delay: 0.5, position: { left: '85%', top: '8%' } },
    { icon: <CheckCircle className="w-6 h-6" />, delay: 1, position: { left: '20%', top: '25%' } },
    { icon: <Clock className="w-5 h-5" />, delay: 1.5, position: { left: '75%', top: '30%' } },
    { icon: <BarChart2 className="w-5 h-5" />, delay: 2, position: { left: '15%', top: '70%' } },
    { icon: <Settings2 className="w-6 h-6" />, delay: 2.5, position: { left: '80%', top: '65%' } },
  ];

  return (
    <div className="login-container-v2" ref={containerRef}>
      {/* Background Elements */}
      <div className="background-wrapper">
        <div className="floating-waves">
          <div className="floating-wave wave-1" />
          <div className="floating-wave wave-2" />
          <div className="floating-wave wave-3" />
        </div>
        
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
        <div className="gradient-orb orb-3" />
        <div className="gradient-orb orb-4" />
        
        <div className="grid-pattern" />
        
        <div className="floating-elements">
          {floatingElements.map((element, index) => (
            <motion.div
              key={index}
              className="floating-element"
              initial={{ opacity: 0, scale: 0, rotate: 0 }}
              animate={{ 
                opacity: [0.3, 0.8, 0.3],
                scale: [0.8, 1.2, 0.8],
                rotate: [0, 360],
                x: [0, Math.random() * 40 - 20, 0],
                y: [0, Math.random() * 40 - 20, 0],
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                delay: element.delay,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
              style={{
                left: element.position.left,
                top: element.position.top,
              }}
            >
              {element.icon}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <motion.div
        className="login-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left Side - Branding */}
        <motion.div 
          className="branding-section"
          style={{
            transform: `translateX(${mousePosition.x * 10}px) translateY(${mousePosition.y * 10}px)`
          }}
        >
          <motion.div className="logo-container" variants={itemVariants}>
            <div className="logo-wrapper">
              <img src={`/AMBTU_logo.svg`} alt="AmbTu" className="logo" />
              <motion.div 
                className="logo-glow"
                animate={{
                  scale: [1, 1.02, 1],
                  opacity: [0.15, 0.25, 0.15]
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </div>
          </motion.div>

          <motion.h1 className="brand-title" variants={itemVariants}>
            <span className="gradient-text">Nueva Contraseña Segura</span>
          </motion.h1>

          <motion.p className="brand-subtitle" variants={itemVariants}>
            Crea una contraseña fuerte que cumpla con nuestros requisitos de seguridad
          </motion.p>

          {/* Feature Cards */}
          <motion.div className="feature-cards" variants={itemVariants}>
            <motion.div 
              className="feature-card"
              whileHover={{ scale: 1.02, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <Lock className="feature-icon" />
              <span>Seguridad Máxima</span>
            </motion.div>
            <motion.div 
              className="feature-card"
              whileHover={{ scale: 1.02, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <Shield className="feature-icon" />
              <span>Protección Avanzada</span>
            </motion.div>
            <motion.div 
              className="feature-card"
              whileHover={{ scale: 1.02, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <CheckCircle className="feature-icon" />
              <span>Validación en Tiempo Real</span>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Right Side - Form */}
        <motion.div 
          className="form-section login-form-section"
          variants={itemVariants}
          style={{
            transform: `translateX(${mousePosition.x * -5}px) translateY(${mousePosition.y * -5}px)`
          }}
        >
          <div className="glass-card">
            {!success ? (
              <>
                <motion.div className="card-header" variants={itemVariants}>
                  <motion.h2 className="form-title">
                    Restablecer Contraseña
                  </motion.h2>
                  <motion.p className="form-subtitle">
                    Ingresa tu nueva contraseña. Asegúrate de que sea segura y fácil de recordar.
                  </motion.p>
                </motion.div>

                <form onSubmit={handleSubmit} className="login-form">
                  {/* Campo de Nueva Contraseña */}
                  <motion.div 
                    className={`form-group ${focusedField === 'password' ? 'focused' : ''}`}
                    variants={itemVariants}
                  >
                    <div className="input-wrapper">
                      <Lock className="input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Nueva contraseña"
                        required
                        className="form-input"
                        disabled={!userId || !token}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="toggle-password"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <motion.div 
                        className="input-glow"
                        animate={{
                          opacity: focusedField === 'password' ? 0.3 : 0
                        }}
                      />
                    </div>
                  </motion.div>

                  {/* Campo de Confirmar Contraseña */}
                  <motion.div 
                    className={`form-group ${focusedField === 'confirmPassword' ? 'focused' : ''}`}
                    variants={itemVariants}
                  >
                    <div className="input-wrapper">
                      <Lock className="input-icon" />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onFocus={() => setFocusedField('confirmPassword')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Confirmar contraseña"
                        required
                        className="form-input"
                        disabled={!userId || !token}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="toggle-password"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <motion.div 
                        className="input-glow"
                        animate={{
                          opacity: focusedField === 'confirmPassword' ? 0.3 : 0
                        }}
                      />
                    </div>
                  </motion.div>

                  {/* Validaciones de Contraseña */}
                  {passwordRequirementsBlock}

                  <AnimatePresence>
                    {error && (
                      <motion.div
                        className="error-message"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <AlertCircle size={16} />
                        <span>{error}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    type="submit"
                    className="submit-button"
                    variants={itemVariants}
                    whileHover={{ scale: allValidationsPassed ? 1.02 : 1 }}
                    whileTap={{ scale: allValidationsPassed ? 0.98 : 1 }}
                    disabled={loading || !allValidationsPassed || !userId || !token}
                  >
                    <span className="button-content">
                      {loading ? (
                        <motion.div
                          className="loading-spinner"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <>
                          <span>Restablecer Contraseña</span>
                          <ChevronRight className="button-icon" />
                        </>
                      )}
                    </span>
                    <div className="button-glow" />
                  </motion.button>

                  <motion.div className="form-footer" variants={itemVariants}>
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="back-to-login"
                    >
                      Volver al inicio de sesión
                    </button>
                  </motion.div>
                </form>
              </>
            ) : (
              <motion.div
                className="success-container"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  className="success-icon-wrapper"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2 
                  }}
                >
                  <div className="success-icon">
                    <Check size={48} />
                  </div>
                </motion.div>

                <motion.h2 
                  className="success-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  ¡Contraseña Actualizada!
                </motion.h2>

                <motion.p 
                  className="success-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  Tu contraseña ha sido actualizada exitosamente.
                </motion.p>

                <motion.p 
                  className="success-submessage"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  Serás redirigido al inicio de sesión en unos segundos...
                </motion.p>

                <motion.button
                  onClick={() => navigate('/login')}
                  className="submit-button"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="button-content">
                    <span>Ir al Inicio de Sesión</span>
                    <ChevronRight className="button-icon" />
                  </span>
                  <div className="button-glow" />
                </motion.button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom Wave */}
      <div className="bottom-wave">
        <div className="bottom-wave-additional"></div>
        <svg viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path 
            fill="url(#wave-gradient)" 
            fillOpacity="1" 
            d="M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,213.3C672,192,768,128,864,128C960,128,1056,192,1152,213.3C1248,235,1344,213,1392,202.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
          <defs>
            <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#8b4513" stopOpacity="0.15" />
              <stop offset="50%" stopColor="#a0522d" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#8b4513" stopOpacity="0.15" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default ResetPassword;

